import React, { useState, useEffect } from 'react';
import { Row, Col, Checkbox, Typography, Button, Input, Modal, Tooltip, Spin, Flex } from 'antd';
import { LoadingOutlined, UsergroupDeleteOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { updatePermissionGroups } from '../../actions/PermissionGroups';
import remove from '../../Res/Icon/permissionGroupsDelete.svg';
import './permissions.css';
import { PERMISSIONS, RESTRICTED_PERMISSION_GROUPS } from '../../utils/constants';
import loadingSpinner from '../../Res/logo-loading.gif';
import { toast } from 'react-toastify';
import { getAllPermissionGroups } from '../../actions/PermissionGroups';


const { Title } = Typography;
const styles = { loadingSpinner: { height: '640px', display: 'flex', alignItems: 'center', justifyContent: 'center' } };

const PermissionsUI = () => {
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [originalState, setOriginalState] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const user = useSelector(state => state.AppState.user)
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [isDuplicateGroup, setIsDuplicateGroup] = useState(false);
  const token = localStorage.getItem('jwtToken') || null;
  const [isLoading, setIsLoading] = useState(true)
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchPermissionGroups = async () => {
      try {
        //fetch all permission groups from backend
        const allPermissionGroups = await dispatch(getAllPermissionGroups(token))

        if (Object.keys(allPermissionGroups).length > 0) {
          const data = allPermissionGroups;
          const groups = Object.keys(data); 
    
          // Ensure newGroups are included with default false read/write
          const initialPermissions = groups.reduce((acc, group) => {
            const permissions = data[group] || {}; 
            acc[group] = Object.keys(PERMISSIONS).reduce((obj, perm) => {
              obj[perm] = {
                read: permissions[perm]?.read || false,
                write: permissions[perm]?.write || false,
              };
              return obj;
            }, {});
            return acc;
          }, {});
          setPermissionGroups(groups)
          // sort permissions alphabatically
          const sortedValues = Object.entries(PERMISSIONS).sort((a, b) => {
            if (a[1] < b[1]) return -1;
            if (a[1] > b[1]) return 1;
            return 0;
          });
          const sortedData = Object.fromEntries(sortedValues);
          setPermissions(Object.keys(sortedData));
          // setPermissions(Object.keys(PERMISSIONS));
          setSelectedPermissions(initialPermissions);
          setOriginalState({ permissionGroups: groups, selectedPermissions: initialPermissions });
          setIsLoading(false)
        }
      } catch (error) {
        console.error("Error fetching permission groups:", error);
      }
    };
    
    if(user?.epikEngineering != undefined && user?.epikEngineering == true){
      fetchPermissionGroups();
    }
      
  }, [user]);

  useEffect(() =>{
    if(user?.epikEngineering != undefined && user?.epikEngineering != true){
      setIsLoading(false)
      toast.error( `You don't have permission to access Permission Groups`);
    }
  },[user])

  const detectChanges = (updatedGroups, updatedPermissions) => {
    return (
      JSON.stringify(updatedGroups) !== JSON.stringify(originalState.permissionGroups) ||
      JSON.stringify(updatedPermissions) !== JSON.stringify(originalState.selectedPermissions)
    );
  };

  const handleCheckboxChange = (group, permission, type) => {
    const updatedPermissions = {
      ...selectedPermissions,
      [group]: {
        ...selectedPermissions[group],
        [permission]: {
          ...selectedPermissions[group]?.[permission],
          [type]: !selectedPermissions[group]?.[permission]?.[type] || false,
        },
      },
    };
    setSelectedPermissions(updatedPermissions);
    setHasChanges(detectChanges(permissionGroups, updatedPermissions));
  };
  

  const getGroupColorClass = index => `color-${index % 6}`;

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await dispatch(updatePermissionGroups(token, selectedPermissions));
      setOriginalState({ permissionGroups, selectedPermissions });
      setHasChanges(false);
    } catch (error) {
      console.error('Error updating permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGroup = (group) => {
    const updatedGroups = permissionGroups.filter((g) => g !== group);
    const updatedPermissions = { ...selectedPermissions };
    delete updatedPermissions[group];

    setPermissionGroups(updatedGroups);
    setSelectedPermissions(updatedPermissions);
    setHasChanges(detectChanges(updatedGroups, updatedPermissions));
  };

  const handleAddPermissionGroup = () => {
    if (newGroupName && !isDuplicateGroup) {
      const updatedGroups = [...permissionGroups, newGroupName];
      const updatedPermissions = {
        ...selectedPermissions,
        [newGroupName]: permissions.reduce((acc, perm) => {
          acc[perm] = { read: false, write: false };
          return acc;
        }, {}),
      };

      setPermissionGroups(updatedGroups);
      setSelectedPermissions(updatedPermissions);
      setNewGroupName('');
      setIsGroupModalOpen(false);
      setHasChanges(detectChanges(updatedGroups, updatedPermissions));
    }
  };

  const handleGroupNameChange = (value) => {
    const lowerCaseGroups = permissionGroups.map(group => group.toLowerCase()); 
    setIsDuplicateGroup(lowerCaseGroups.includes(value.toLowerCase())); 
    setNewGroupName(value);
  };

  if (isLoading) {
    return (
      <div style={styles.loadingSpinner}>
        <img src={loadingSpinner} alt="loading" className="pb-4" height={150} />
      </div>
    );
  }

  return (
    <div className="permissions-container">
      {Object.keys(permissions).length > 0 && (
        <Row justify="end">
          {hasChanges && (
          <Button
            type="primary"
            className="permissions-update-btn"
            onClick={handleUpdate}
            disabled={loading}
          >
            {loading ? 
            <Spin
            indicator={<LoadingOutlined style={{ fontSize: 16, color: 'white' }} spin />}
            size="small"
          />
          : 'Update'}
          </Button>
        )}
          {/* <Button
            type="primary"
            onClick={() => setIsGroupModalOpen(true)}
            className="permissions-action-btn"
          >
            Add Permission Group
          </Button> */}
        </Row>
      )}

      <Row>
        <Col span={24} flex="auto">
          <div style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
            <Row style={{ display: 'flex', flexWrap: 'nowrap' }}>
              <Col span={4} className="permissions-column">
                {Object.keys(permissions).length > 0 && (
                  <Row className="permissions-title-row">
                    <Col span={24}>
                      <Title className="permissions-title" level={4}>
                        Permissions
                      </Title>
                    </Col>
                  </Row>
                )}
              </Col>
            {permissionGroups.map((group, index) => (
              <Col key={group} className="group-column-header">
                <Row 
                  className={`group-column-title-wrapper ${getGroupColorClass(index)}`} 
                >
                  <Tooltip title={group} placement="topLeft">
                    <Title
                      level={5}
                      className="group-column-title"
                      style={{ 
                        maxWidth: !RESTRICTED_PERMISSION_GROUPS.includes(group) ? 'calc(100% - 30px)' : '100%'
                      }}
                    >
                      {group}
                    </Title>
                  </Tooltip>
                  
                  {!RESTRICTED_PERMISSION_GROUPS.includes(group) && (
                    <Tooltip title="Remove Permission Group" placement="topLeft">
                      <span 
                        onClick={() => handleRemoveGroup(group)}
                      >
                        <img src={remove} className="remove-icon" />
                      </span>
                    </Tooltip>
                  )}
                </Row>
      
                <Row className={'group-column-read-write-checkbox'}>
                  <Col span={12} className="label-column">Read</Col>
                  <Col span={12} className="label-column">Write</Col>
                </Row>
          </Col>
          ))}
        </Row>

            {permissions.map((permission, index) => (
              <Row key={permission} style={{ display: 'flex', flexWrap: 'nowrap' }}>
                <Col span={4} className="permission-text">
                  <Tooltip title={PERMISSIONS[permission]} placement="topLeft">
                    {PERMISSIONS[permission]}
                    {/* _ {index + 1} */}
                  </Tooltip>
                </Col>
                {permissionGroups.map((group, index) => (
                  <Col
                    key={`${group}-${permission}`}
                    className={`checkbox-column ${getGroupColorClass(index)}`}
                    style={{ minWidth: '121px' }}
                  >
                    <Row justify="center" align="middle">
                      <Col span={12}>
                        <Checkbox
                          className={`${getGroupColorClass(index)}`}
                          checked={selectedPermissions[group]?.[permission]?.read}
                          onChange={() => handleCheckboxChange(group, permission, 'read')}
                        />
                      </Col>
                      <Col span={12}>
                        <Checkbox
                          className={`${getGroupColorClass(index)}`}
                          checked={selectedPermissions[group]?.[permission]?.write}
                          onChange={() => handleCheckboxChange(group, permission, 'write')}
                        />
                      </Col>
                    </Row>
                  </Col>
                ))}
              </Row>
            ))}
          </div>
        </Col>
      </Row>
      <Modal
        title="Add Permission Group"
        visible={isGroupModalOpen}
        onOk={handleAddPermissionGroup}
        onCancel={() => {
          setNewGroupName('');
          setIsDuplicateGroup(false);
          setIsGroupModalOpen(false)
        
        }}
        okButtonProps={{ disabled: !newGroupName || isDuplicateGroup }}
      >
        <Input
          placeholder="Enter group name"
          value={newGroupName}
          onChange={(e) => handleGroupNameChange(e.target.value)}
        />
        {isDuplicateGroup && (
          <Typography.Text type="danger">
            This group name already exists. Please choose a unique name.
          </Typography.Text>
        )}
      </Modal>
    </div>
  );
};

export default PermissionsUI;